import React, { memo, useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import cn from 'classnames';
import pick from 'lodash/pick';

import { getMeetingItemByUuid } from 'Shared/selectors/common';
import VideoPlayer from 'Shared/components/VideoPlayer/VideoPlayer';
import RoundCheckbox from 'Shared/components/Checkboxes/RoundCheckbox/RoundCheckbox';
import { addMeetingItemAttachment, toggleTodo } from 'Shared/actions/todos';
import { WorkspaceItem, ItemType } from 'Shared/constants/types';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import ImageDndUploader from 'Shared/components/ImageDndUploader/ImageDndUploader';
import MobileContext from 'Shared/contexts/MobileContext';
import WorkspaceItemAttachments from './Attachments/WorkspaceItemAttachments';

import AssigneeInput from 'Shared/components/AssigneeInput/AssigneeInput';
import ActionsArea from './ActionsArea/ActionsArea';
import IsFileUploadingLoader from './IsFileUploadingLoader/IsFileUploadingLoader';
import NotificationBar from './NotificationBar/NotificationBar';
import ItemEditorWrapper from './ItemEditorWrapper/ItemEditorWrapper';

import '@workpatterns/draft-js-mention-plugin/lib/plugin.css';
import 'draft-js/dist/Draft.css';
import './WorkspaceItemContainer.scss';

const neededItemFields = Object.freeze([
  'itemType',
  'completed',
  'uuid',
  'parentUuid',
  'showPreview',
  'isSection',
  'video',
  'attachments',
  'showPreview',
  'isShowLoader',
  'id',
  'assignmentId',
  'assignee',
]);

type Props = {
  isDisabled?: boolean;
  isCompleteToggleDisabled?: boolean;
  parentIsSection?: boolean;
  uuid: string;
  isSelected: boolean;
  isFocused: boolean;
  notShowAsChild: boolean;
  notInGroupSelect: boolean;
  hideActionsAreaOnDesktop?: boolean;
  onAddItem: Function;
  nestItemByTabClick?: Function;
  onArrowUpClick: Function;
  onArrowDownClick: Function;
  onFocusedTodoUpdate?: Function;
  onHideAnimationTrigger?: Function;
  forceShowMobileActions?: boolean;
  customActionsArea?: Function;
};

function WorkspaceItemContainer({
  isDisabled = false,
  isCompleteToggleDisabled = false,
  parentIsSection = false,
  uuid: itemUuid,
  isSelected,
  isFocused,
  notShowAsChild = false,
  hideActionsAreaOnDesktop = false,
  notInGroupSelect,
  onAddItem,
  nestItemByTabClick,
  onArrowUpClick,
  onArrowDownClick,
  onFocusedTodoUpdate,
  onHideAnimationTrigger,
  forceShowMobileActions = false,
  customActionsArea,
}: Props) {
  const dispatch = useDispatch();

  const todoData: WorkspaceItem = useDeepEqualSelector(state =>
    pick(getMeetingItemByUuid(itemUuid)(state), neededItemFields)
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { itemType, isSection, completed, uuid, attachments, assignee, assignmentId, parentUuid, video, id } = todoData;
  const [togglingComplete, setTogglingCompleted] = useState(completed);
  const [isShowLoader, setIsShowLoader] = useState(todoData.isShowLoader || false);
  const isMobile = useContext(MobileContext);
  const { tr } = useTranslation();

  const toggleTimeout = useRef<number>(null);
  const hideAnimationTimeout = useRef<number>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef(null);

  const assingeeName = assignee?.personName;

  useEffect(() => {
    return () => {
      if (toggleTimeout.current) clearTimeout(toggleTimeout.current);
      if (hideAnimationTimeout.current) clearTimeout(hideAnimationTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (completed !== togglingComplete) setTogglingCompleted(completed);
    if (isShowLoader !== todoData.isShowLoader) setIsShowLoader(todoData.isShowLoader);
  }, [completed, todoData.isShowLoader]);

  const handleEditorContainerClick = useCallback(() => {
    if (isDisabled) return null;
    if ((isSelected || isFocused) && notInGroupSelect) return null;

    dispatch(updateSelectedUuids(uuid, { isOneSelected: true }));
    if (onFocusedTodoUpdate) onFocusedTodoUpdate(uuid);
    return null;
  }, [uuid, isSelected, isFocused, isDisabled, notInGroupSelect, onFocusedTodoUpdate, dispatch]);

  const fileUploadProccess = useCallback(
    async file => {
      setIsShowLoader(true);
      const attachmentUuid = uuidv4();
      await dispatch(addMeetingItemAttachment({ uuid, file, itemType, attachmentUuid }));
      setIsShowLoader(false);
    },
    [uuid, itemType, dispatch]
  );

  const handleAttachmentInputChange = useCallback(
    event => {
      if (event.target.files.length === 0) return null;
      const file = event.target.files[0];
      return fileUploadProccess(file);
    },
    [fileUploadProccess]
  );

  const handleToggleCompleted = useCallback(() => {
    setTogglingCompleted(!togglingComplete);
    if (toggleTimeout.current) clearTimeout(toggleTimeout.current);
    if (hideAnimationTimeout.current) clearTimeout(hideAnimationTimeout.current);
    if (onHideAnimationTrigger && !togglingComplete)
      hideAnimationTimeout.current = window.setTimeout(onHideAnimationTrigger, 200);

    const timeoutDispatch = onHideAnimationTrigger ? 800 : 400;
    toggleTimeout.current = window.setTimeout(() => {
      dispatch(toggleTodo({ id, uuid, itemType }, !togglingComplete));
    }, timeoutDispatch);
  }, [togglingComplete, id, uuid, dispatch, itemType, onHideAnimationTrigger]);

  const handleFileDrop = useCallback(files => files.forEach(file => fileUploadProccess(file)), [fileUploadProccess]);

  const isActionItem = itemType === ItemType.actionItem;
  const isDiscussionTopic = itemType === ItemType.discussionTopic;

  const CustomActionAreaJsx = () => {
    const CustomActions = customActionsArea || null;
    return <CustomActions itemUuid={itemUuid} />;
  };

  const baseClassName = 'workspace-item-container';

  return (
    <div className={baseClassName}>
      <div className={`d-flex ${baseClassName}__print-wrapper`} ref={containerRef}>
        {isShowLoader && <IsFileUploadingLoader />}
        {parentUuid && !notShowAsChild && !parentIsSection && <div className="workspace-item-container__spacer" />}
        {isSection && !isActionItem ? null : (
          <RoundCheckbox
            className="mr-2 ml-sm-1"
            checked={togglingComplete}
            onToggle={isCompleteToggleDisabled ? null : handleToggleCompleted}
          />
        )}
        {isDiscussionTopic && (
          <div className={`${baseClassName}__assignee`}>
            <AssigneeInput
              uuid={uuid}
              avatarOnly
              tooltip={
                assignmentId
                  ? tr('perform.workspace_item_container.assignee_input.assigned_to_with_name', assingeeName || '')
                  : tr('perform.workspace_item_container.assignee_input.set_discussion_leader')
              }
            />
          </div>
        )}
        {!isMobile && <NotificationBar itemUuid={uuid} parentUuid={parentUuid} />}
        <div className="d-flex flex-column w-100">
          <ImageDndUploader onFileDrop={handleFileDrop}>
            <div
              className={cn('workspace-item-container-editor-wrapper', {
                'workspace-item-container-editor-wrapper--with-video': video,
                'workspace-item-container-editor-wrapper--is-top-level': !isActionItem && !parentUuid && !isSection,
                'workspace-item-container-editor-wrapper--is-section': !isActionItem && isSection,
              })}
              onClick={handleEditorContainerClick}
            >
              <ItemEditorWrapper
                editorRef={editorRef}
                isDisabled={isDisabled}
                uuid={itemUuid}
                isShowLoader={isShowLoader}
                onAddItem={onAddItem}
                containerRef={containerRef}
                isFocused={isFocused || isSelected}
                nestItemByTabClick={nestItemByTabClick}
                onArrowUpClick={onArrowUpClick}
                onArrowDownClick={onArrowDownClick}
                fileUploadProccess={fileUploadProccess}
                hideActionsAreaOnDesktop={hideActionsAreaOnDesktop}
              />
              {video && (
                <VideoPlayer
                  videoID={video.id}
                  sources={[video.mp4Url, video.url]}
                  previewImageURL={video.previewImageUrl}
                  transcription={video.transcription}
                  audioOnly={video.audioOnly}
                />
              )}
            </div>
          </ImageDndUploader>
        </div>

        {!isDisabled && (
          <>
            {customActionsArea ? (
              <CustomActionAreaJsx />
            ) : (
              <ActionsArea
                uuid={uuid}
                parentUuid={parentUuid}
                isActionItem={isActionItem}
                hideActionsAreaOnDesktop={hideActionsAreaOnDesktop}
                isSection={isSection}
                onHideAnimationTrigger={onHideAnimationTrigger}
                onAttachmentInputChange={handleAttachmentInputChange}
                onArrowUpClick={onArrowUpClick}
                forceShowMobileActions={forceShowMobileActions}
              />
            )}
          </>
        )}
      </div>
      <div
        className={cn('workspace-item-container-attachments-list w-100', {
          'workspace-item-container-attachments-list--is-children': parentUuid && (!isActionItem || !notShowAsChild),
        })}
      >
        <WorkspaceItemAttachments workspaceItem={todoData} />
      </div>
    </div>
  );
}

export default memo(WorkspaceItemContainer);
